// Environment utility functions
// Provides consistent environment detection and configuration across the app

export const isDevelopment = () => {
  return import.meta.env.DEV || import.meta.env.VITE_APP_ENV === 'development'
}

export const isProduction = () => {
  return import.meta.env.PROD || import.meta.env.VITE_APP_ENV === 'production'
}

export const getApiBaseUrl = () => {
  if (import.meta.env.VITE_API_BASE_URL) return import.meta.env.VITE_API_BASE_URL
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID
  const region = import.meta.env.VITE_FIREBASE_REGION || 'us-central1'
  if (!projectId) throw new Error('Missing VITE_FIREBASE_PROJECT_ID for API base URL')
  return `https://${region}-${projectId}.cloudfunctions.net`
}

export const getFunctionsUrl = () => {
  if (import.meta.env.VITE_FUNCTIONS_URL) return import.meta.env.VITE_FUNCTIONS_URL
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID
  const region = import.meta.env.VITE_FIREBASE_REGION || 'us-central1'
  if (!projectId) throw new Error('Missing VITE_FIREBASE_PROJECT_ID for Functions URL')
  return `https://${region}-${projectId}.cloudfunctions.net`
}

export const getDownloadFunctionUrl = () => {
  return import.meta.env.VITE_DOWNLOAD_FUNCTION_URL || ''
}

export const shouldEnableDebugMenu = () => {
  // On everywhere EXCEPT the hardened production build (`npm run build:prod`).
  // The dev/test app (this Firebase project) keeps the debug menu, role
  // switcher, and testing tabs so deployed features can be debugged.
  return import.meta.env.VITE_PROD_HARDENED !== true
}

export const shouldEnableConsoleLogs = () => {
  return isDevelopment() || import.meta.env.VITE_ENABLE_CONSOLE_LOGS === 'true'
}

export const shouldUseFirebaseEmulators = () => {
  return import.meta.env.VITE_USE_FIREBASE_EMULATORS === 'true' && isDevelopment()
}

/**
 * Evaluation records UI + SEIS Pending → eval write.
 *
 * Explicit `VITE_FEATURE_EVALS` wins. Unset means: on for hosted dev
 * (not hardened), off for school production (`deploy:school` /
 * `build:prod`). That lets Vacaville take feedback/email/504-accommodations
 * without staff seeing the unfinished eval surfaces, while casemangervue
 * keeps testing them. Flip the env var and redeploy to turn a school on.
 *
 * Client-only. Rules still allow writes — the doors are the nav, route,
 * student-form card, roster tile, and the SEIS pending step.
 */
export function resolveEvaluationsEnabled({ featureFlag, prodHardened } = {}) {
  const raw = String(featureFlag ?? '')
    .trim()
    .toLowerCase()
  if (raw === 'true' || raw === '1' || raw === 'on' || raw === 'yes') return true
  if (raw === 'false' || raw === '0' || raw === 'off' || raw === 'no') return false
  return prodHardened !== true
}

export function areEvaluationsEnabled() {
  return resolveEvaluationsEnabled({
    featureFlag: import.meta.env.VITE_FEATURE_EVALS,
    prodHardened: import.meta.env.VITE_PROD_HARDENED
  })
}

// Enhanced console logging that respects environment settings
export const devLog = (...args) => {
  if (shouldEnableConsoleLogs()) {
    console.log('[DEV]', ...args)
  }
}

export const devWarn = (...args) => {
  if (shouldEnableConsoleLogs()) {
    console.warn('[DEV]', ...args)
  }
}

export const devError = (...args) => {
  if (shouldEnableConsoleLogs()) {
    console.error('[DEV]', ...args)
  }
}

// Environment information for debugging
export const getEnvironmentInfo = () => {
  return {
    isDevelopment: isDevelopment(),
    isProduction: isProduction(),
    mode: import.meta.env.MODE,
    apiBaseUrl: getApiBaseUrl(),
    functionsUrl: getFunctionsUrl(),
    downloadFunctionUrl: getDownloadFunctionUrl(),
    debugMenuEnabled: shouldEnableDebugMenu(),
    consoleLogsEnabled: shouldEnableConsoleLogs(),
    firebaseEmulatorsEnabled: shouldUseFirebaseEmulators(),
    evaluationsEnabled: areEvaluationsEnabled(),
    firebaseProjectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    buildTimestamp: new Date().toISOString()
  }
}

// Log environment info on app start (development only)
if (isDevelopment()) {
  console.log('🌍 Environment Configuration:', getEnvironmentInfo())
}
